import type { PropertyTaskStateHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyReservationStatesHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyReservationMultiState/propertyReservationState.interface";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { localDateKey } from "@web/composables/Date/localDateKey";
import { shallowReactive } from "vue";

export const CURRENT_KEY = "current";
export const MAP_SEP = "\0";
export const PROPERTY_RESERVATION_STATE_ROOT = "propertyReservationState";
export const PROPERTY_TASK_STATE_ROOT = "propertyTaskState";

export const reservationStateMap = shallowReactive(new Map<string, PropertyReservationStatesHydraItem>());
export const taskStateMap = shallowReactive(new Map<string, PropertyTaskStateHydraItem | null>());

export function reservationQueryKey(locationIri: string, propertyId: number, dateKey: string) {
  return [PROPERTY_RESERVATION_STATE_ROOT, locationIri, propertyId, dateKey] as const;
}

// Reservation state is cached per local day, with "now" as its own entry: every deadline on a day shares one
// request, and "now" is reused while fresh instead of being refetched under a new timestamp on every call.
export function reservationDateKey(isoDatePoint: string, nowDatePoint: string): string {
  if (isoDatePoint === nowDatePoint) {
    return CURRENT_KEY;
  }
  return localDateKey(isoDatePoint) ?? isoDatePoint;
}

export function taskQueryKey(locationIri: string, propertyId: number) {
  return [PROPERTY_TASK_STATE_ROOT, locationIri, propertyId] as const;
}

export function reservationMapKey(locationIri: string, propertyId: number, dateKey: string): string {
  return `${locationIri}${MAP_SEP}${propertyId}${MAP_SEP}${dateKey}`;
}

export function taskMapKey(locationIri: string, propertyId: number): string {
  return `${locationIri}${MAP_SEP}${propertyId}`;
}

export function parseReservationMapKey(key: string): { locationIri: string, propertyId: number, dateKey: string } {
  const [locationIri, propertyId, dateKey] = key.split(MAP_SEP);
  return { locationIri, propertyId: Number(propertyId), dateKey };
}

export function fillReservationMap(
  locationIri: string,
  propertyId: number,
  isoDatePoint: string,
  state: PropertyReservationStatesHydraItem,
): void {
  const dateKey = state.datePointAt === null
    ? CURRENT_KEY
    : (localDateKey(state.datePointAt) ?? localDateKey(isoDatePoint) ?? CURRENT_KEY);
  reservationStateMap.set(reservationMapKey(locationIri, propertyId, dateKey), state);
}

export function clearPropertyStateMaps(): void {
  reservationStateMap.clear();
  taskStateMap.clear();
}

export function hydratePropertyStateMapsFromQueryClient(): void {
  const client = getQueryClient();

  for (const [queryKey, data] of client.getQueriesData<PropertyReservationStatesHydraItem>({
    queryKey: [PROPERTY_RESERVATION_STATE_ROOT],
  })) {
    if (!data) {
      continue;
    }
    const [, locationIri, propertyId, dateKey] = queryKey;
    if (typeof locationIri !== "string" || typeof propertyId !== "number" || typeof dateKey !== "string") {
      continue;
    }
    fillReservationMap(locationIri, propertyId, dateKey, data);
  }

  for (const [queryKey, data] of client.getQueriesData<PropertyTaskStateHydraItem | null>({
    queryKey: [PROPERTY_TASK_STATE_ROOT],
  })) {
    if (data === undefined) {
      continue;
    }
    const [, locationIri, propertyId] = queryKey;
    if (typeof locationIri !== "string" || typeof propertyId !== "number") {
      continue;
    }
    taskStateMap.set(taskMapKey(locationIri, propertyId), data);
  }
}
